import React, { useState } from 'react';
import { AVAILABLE_ROLES } from './constants';

export default function TeamMemberFilters({
  searchTerm,
  setSearchTerm,
  roleFilter,
  setRoleFilter,
  sortField,
  setSortField,
  sortDirection,
  setSortDirection,
  setCurrentPage,
  totalResults
}) {
  const [isExpanded, setIsExpanded] = useState(false);
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1); // Reset to first page when searching
  };

  const handleRoleFilterChange = (e) => {
    setRoleFilter(e.target.value);
    setCurrentPage(1); // Reset to first page when filtering
  };

  const handleSortFieldChange = (e) => {
    setSortField(e.target.value);
    setCurrentPage(1); // Reset to first page when sorting
  };

  const handleSortDirectionToggle = () => {
    setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
  };



  const handleClearSearch = () => {
    setSearchTerm('');
    setCurrentPage(1);
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    setRoleFilter('ALL');
    setCurrentPage(1);
  };

  const toggleAccordion = () => {
    setIsExpanded(!isExpanded);
  };

  const hasActiveFilters = searchTerm || roleFilter !== 'ALL';

  return (
    <div className="mb-4">
      {/* Accordion Toggle Button */}
      <button
        onClick={toggleAccordion}
        className={`w-full flex items-center justify-between p-3 bg-gray-50 border border-gray-200 hover:bg-gray-100 transition-colors ${isExpanded ? 'rounded-t-lg' : 'rounded-lg'}`}
        aria-expanded={isExpanded}
      >
        <div className="flex items-center space-x-2">
          <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <span className="text-sm font-medium text-gray-700">
            Search & Filter Options
          </span>
          {hasActiveFilters && (
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-sky-100 text-sky-800">
              Active
            </span>
          )}
        </div>
        <svg 
          className={`w-5 h-5 text-gray-400 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}
          fill="none" 
          stroke="currentColor" 
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* Collapsible Content */}
      <div className={`transition-all duration-300 ease-in-out overflow-hidden ${isExpanded ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'}`}>
        <div className="pt-4 space-y-4 bg-gray-50 px-3 pb-4 rounded-b-lg border-x border-b border-gray-200">
          {/* Search and main filters row */}
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="flex-1 min-w-0">
              <label htmlFor="search" className="block text-sm font-medium text-gray-700 mb-1">
                Search Team Members
              </label>
              <div className="relative rounded-md shadow-sm">
                <input
                  type="text"
                  id="search"
                  value={searchTerm}
                  onChange={handleSearchChange}
                  onKeyDown={(e) => {
                    if (e.key === 'Escape') {
                      handleClearSearch();
                    }
                  }}
                  className="block w-full rounded-md border border-gray-300 py-2 px-3 text-sm 
                           shadow-sm focus:border-sky-500 focus:ring-sky-500 pr-10"
                  placeholder="Name, email, role..."
                  aria-label="Search team members"
                />
                {searchTerm ? (
                  <button
                    type="button"
                    onClick={handleClearSearch}
                    className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-500"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                  </button>
                ) : (
                  <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                    <svg 
                      className="h-4 w-4 text-gray-400" 
                      xmlns="http://www.w3.org/2000/svg" 
                      viewBox="0 0 20 20" 
                      fill="currentColor" 
                      aria-hidden="true"
                    >
                      <path 
                        fillRule="evenodd" 
                        d="M9 3.5a5.5 5.5 0 100 11 5.5 5.5 0 000-11zM2 9a7 7 0 1112.452 4.391l3.328 3.329a.75.75 0 11-1.06 1.06l-3.329-3.328A7 7 0 012 9z" 
                        clipRule="evenodd" 
                      />
                    </svg>
                  </div>
                )}
              </div>
              {searchTerm && (
                <p className="mt-1 text-xs text-gray-500">
                  Found {totalResults} {totalResults === 1 ? 'match' : 'matches'}
                </p>
              )}
            </div>
            
            <div className="w-full sm:w-auto">
              <label htmlFor="roleFilter" className="block text-sm font-medium text-gray-700 mb-1">
                Filter by Role
              </label>
              <select
                id="roleFilter"
                value={roleFilter}
                onChange={handleRoleFilterChange}
                className="block w-full sm:w-40 rounded-md border border-gray-300 py-2 px-3 text-sm shadow-sm focus:border-sky-500 focus:ring-sky-500"
              >
                <option value="ALL">All Roles</option>
                {AVAILABLE_ROLES.map(role => (
                  <option key={role.value} value={role.value}>
                    {role.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
          
          {/* Secondary controls row */}
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="w-full sm:w-auto">
                <label htmlFor="sortField" className="block text-sm font-medium text-gray-700 mb-1">
                  Sort By
                </label>
                <div className="flex items-center space-x-2">
                  <select
                    id="sortField"
                    value={sortField}
                    onChange={handleSortFieldChange}
                    className="block w-full sm:w-36 rounded-md border border-gray-300 py-2 px-3 text-sm shadow-sm focus:border-sky-500 focus:ring-sky-500"
                  >
                    <option value="lastName">Last Name</option>
                    <option value="role">Role</option>
                    <option value="locationPreference">Location</option>
                    <option value="areas">Areas</option>
                  </select>
                  
                  <button
                    onClick={handleSortDirectionToggle}
                    className="p-2 rounded-md hover:bg-gray-100 border border-gray-300"
                    aria-label={sortDirection === 'asc' ? 'Sort descending' : 'Sort ascending'}
                  >
                    {sortDirection === 'asc' ? (
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-gray-500" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                      </svg>
                    ) : (
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-gray-500" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M14.707 12.707a1 1 0 01-1.414 0L10 9.414l-3.293 3.293a1 1 0 01-1.414-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 010 1.414z" clipRule="evenodd" />
                      </svg>
                    )}
                  </button>
                </div>
              </div>

              {/* Clear filters button */}
              {hasActiveFilters && (
                <div className="w-full sm:w-auto flex items-end">
                  <button
                    onClick={handleClearFilters}
                    className="px-3 py-2 text-sm bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 border border-gray-300"
                  >
                    Clear Filters
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
} 